import { X, AlertTriangle, Clock, Activity, Network, Lightbulb } from '@/utils/icons';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge, ServiceStatusBadge } from '@/components/common/StatusBadge';
import { ProfileBadge } from '@/components/common/ProfileBadge';
import { formatTimeAgo } from '@/utils/formatters';
import { useNodeContext } from '@/hooks/useNodeContext';
import { useEffect } from 'react';

interface ProblemDetailDrawerProps {
  nodeId: string | null;
  onClose: () => void;
}

export function ProblemDetailDrawer({ nodeId, onClose }: ProblemDetailDrawerProps) {
  const { context, loading, fetchContext, clearContext } = useNodeContext();

  useEffect(() => {
    if (nodeId) {
      fetchContext(nodeId);
    } else {
      clearContext();
    }
  }, [nodeId, fetchContext, clearContext]);

  if (!nodeId) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="dls-fixed dls-inset-0 dls-bg-black/40 dls-z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="dls-fixed dls-right-0 dls-top-0 dls-bottom-0 dls-w-full dls-max-w-lg dls-bg-background dls-border-l dls-shadow-50 dls-z-50 dls-overflow-y-auto">
        <div className="dls-sticky dls-top-0 dls-bg-background dls-border-b dls-px-6 dls-py-4 dls-flex dls-items-center dls-justify-between dls-z-10">
          <h2 className="dls-text-lg dls-font-semibold">Problem Detail</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="dls-h-5 dls-w-5" />
          </Button>
        </div>

        {loading ? (
          <div className="dls-flex dls-items-center dls-justify-center dls-py-20">
            <div className="dls-animate-spin dls-rounded-full dls-h-8 dls-w-8 dls-border-b-2 dls-border-primary" />
          </div>
        ) : context ? (
          <div className="dls-p-6 dls-space-y-6">
            {/* Node Overview */}
            <div>
              <div className="dls-flex dls-items-center dls-gap-3 dls-mb-2">
                <Link
                  to={`/nodes/${context.node.id}`}
                  className="dls-text-xl dls-font-bold hover:dls-underline"
                >
                  {context.node.name}
                </Link>
              </div>
              <div className="dls-flex dls-items-center dls-gap-2 dls-flex-wrap">
                <StatusBadge state={context.node.currentNodeState} />
                <ProfileBadge profileName={context.node.profileName} />
                {context.node.portfolio && (
                  <Badge variant="outline">{context.node.portfolio}</Badge>
                )}
                {context.node.portfolioSource && (
                  <Badge variant="secondary" className="dls-text-xs">
                    via {context.node.portfolioSource}
                  </Badge>
                )}
              </div>
              {context.node.lastContactedAt && (
                <p className="dls-text-sm dls-text-muted-foreground dls-mt-2">
                  Last contact: {formatTimeAgo(context.node.lastContactedAt)}
                </p>
              )}
            </div>

            {/* Action Plan */}
            {context.suggestedActions.length > 0 && (
              <Card className="dls-border-status-warning-tertiary dls-bg-status-warning-quarternary/50">
                <CardHeader className="dls-pb-2">
                  <CardTitle className="dls-text-sm dls-flex dls-items-center dls-gap-2 dls-text-status-warning">
                    <Lightbulb className="dls-h-4 dls-w-4" />
                    Suggested Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="dls-space-y-2">
                    {context.suggestedActions.map((action, i) => (
                      <li key={i} className="dls-text-sm dls-text-status-warning dls-flex dls-gap-2">
                        <span className="dls-text-status-warning dls-mt-0.5">•</span>
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Cloud Connectors */}
            {context.cloudConnectors.length > 0 && (
              <Card>
                <CardHeader className="dls-pb-2">
                  <CardTitle className="dls-text-sm dls-flex dls-items-center dls-gap-2">
                    <Network className="dls-h-4 dls-w-4" />
                    Cloud Connectors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="dls-space-y-2">
                    {context.cloudConnectors.map((cc) => (
                      <div key={cc.name} className="dls-flex dls-items-center dls-justify-between dls-text-sm">
                        <span className="dls-font-medium">{cc.name}</span>
                        <StatusBadge state={cc.status} />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Services */}
            {context.services.length > 0 && (
              <Card>
                <CardHeader className="dls-pb-2">
                  <CardTitle className="dls-text-sm dls-flex dls-items-center dls-gap-2">
                    <Activity className="dls-h-4 dls-w-4" />
                    Services ({context.services.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="dls-space-y-2">
                    {context.services.map((svc) => (
                      <div key={svc.id} className="dls-flex dls-items-center dls-justify-between dls-text-sm">
                        <span className="dls-font-medium dls-truncate">{svc.name}</span>
                        <div className="dls-flex dls-items-center dls-gap-2">
                          <span className="dls-text-xs dls-text-muted-foreground">
                            {svc.runningContainers ?? '—'}
                          </span>
                          <ServiceStatusBadge status={svc.healthStatus} className="dls-text-xs" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notification Timeline */}
            {context.notifications.length > 0 && (
              <Card>
                <CardHeader className="dls-pb-2">
                  <CardTitle className="dls-text-sm dls-flex dls-items-center dls-gap-2">
                    <AlertTriangle className="dls-h-4 dls-w-4" />
                    Event Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="dls-space-y-3 dls-max-h-[300px] dls-overflow-y-auto">
                    {context.notifications.map((notif) => (
                      <div key={notif.id} className="dls-border-l-2 dls-border-muted dls-pl-3 dls-pb-1">
                        <div className="dls-flex dls-items-center dls-gap-2">
                          <span className="dls-text-xs dls-text-muted-foreground">
                            {formatTimeAgo(notif.occurredAt)}
                          </span>
                          {notif.displayType && (
                            <Badge variant="outline" className="dls-text-xs">
                              {notif.displayType}
                            </Badge>
                          )}
                        </div>
                        <p className="dls-text-sm dls-mt-0.5">{notif.message || notif.type}</p>
                        {notif.serviceName && (
                          <p className="dls-text-xs dls-text-muted-foreground">
                            Service: {notif.serviceName}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Activities */}
            {context.activities.length > 0 && (
              <Card>
                <CardHeader className="dls-pb-2">
                  <CardTitle className="dls-text-sm dls-flex dls-items-center dls-gap-2">
                    <Clock className="dls-h-4 dls-w-4" />
                    Recent Platform Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="dls-space-y-2 dls-max-h-[200px] dls-overflow-y-auto">
                    {context.activities.map((act) => (
                      <div key={act.id} className="dls-text-sm">
                        <div className="dls-flex dls-items-center dls-gap-2">
                          <span className="dls-text-xs dls-text-muted-foreground">
                            {formatTimeAgo(act.occurredAt)}
                          </span>
                          {act.actorName && (
                            <span className="dls-text-xs dls-font-medium">{act.actorName}</span>
                          )}
                        </div>
                        <p className="dls-text-muted-foreground">{act.description || act.type}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Link to full detail */}
            <div className="dls-pt-2">
              <Link to={`/nodes/${context.node.id}`}>
                <Button variant="secondary" className="dls-w-full">
                  View Full Node Detail
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="dls-flex dls-items-center dls-justify-center dls-py-20 dls-text-muted-foreground">
            Failed to load node context
          </div>
        )}
      </div>
    </>
  );
}
